/**
 * The job form (create and edit) for /wireline-slickline/jobs.
 *
 * The CUSTOMER follows the well: the API takes the well's customer when none is
 * sent (lib/wl-jobs.ts parseJob) and refuses a mismatched pair
 * (well_customer_mismatch), so the form names the well and shows its customer
 * rather than offering a second picker that could disagree.
 *
 * Scopes are run kinds from the catalog; at least one, which the API also
 * refuses without. Dates are calendar days (YYYY-MM-DD), sent as typed.
 */
import React, { useMemo, useState } from 'react';
import type { WlCatalog, WlCatalogEntry } from '../../types/wireline-slickline';
import type { WlJob, WlJobWrite } from '../../types/wireline-slickline-jobs';
import { wlBlankToNull } from '../../lib/wireline-slickline';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WlField, WlFormError } from './WlBits';

export interface WlJobFormWell {
  id: string;
  wellName: string;
  customerName: string | null;
  idText: string | null;
}

export function JobForm({
  job,
  catalog,
  wells,
  users,
  pending,
  error,
  submitLabel,
  allowScheduled,
  onCancel,
  onSubmit,
}: {
  job: WlJob | null;
  catalog: WlCatalog | null;
  wells: WlJobFormWell[];
  users: Array<{ id: string; name: string }>;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  /** Create only: a job may be created as scheduled. */
  allowScheduled?: boolean;
  onCancel: () => void;
  onSubmit: (body: WlJobWrite) => void;
}) {
  const [wellId, setWellId] = useState(job?.wellId ?? '');
  const [scopes, setScopes] = useState<string[]>(job?.scopes ?? []);
  const [status, setStatus] = useState<'draft' | 'scheduled'>('draft');
  const [ticketMode, setTicketMode] = useState(job?.ticketMode ?? '');
  const [supervisorId, setSupervisorId] = useState(job?.supervisorId ?? '');
  const [start, setStart] = useState(job?.plannedStartOn ?? '');
  const [end, setEnd] = useState(job?.plannedEndOn ?? '');
  const [description, setDescription] = useState(job?.description ?? '');
  const [po, setPo] = useState(job?.poNumber ?? '');
  const [afe, setAfe] = useState(job?.afeNumber ?? '');
  const [costCode, setCostCode] = useState(job?.costCode ?? '');
  const [notes, setNotes] = useState(job?.notes ?? '');
  const [localError, setLocalError] = useState<string | null>(null);
  const kinds: WlCatalogEntry[] = catalog?.runKinds ?? [];
  const well = useMemo(() => wells.find((w) => w.id === wellId) ?? null, [wells, wellId]);

  const toggle = (v: string) => setScopes((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));

  return (
    <form
      className="space-y-4 px-4 py-4"
      onSubmit={(e) => {
        e.preventDefault();
        setLocalError(null);
        if (!wellId) return setLocalError('Choose the well.');
        if (scopes.length === 0) return setLocalError('Choose at least one run kind the job covers.');
        const body: WlJobWrite = {
          well_id: wellId,
          scopes,
          supervisor_id: supervisorId || null,
          planned_start_on: start || null,
          planned_end_on: end || null,
          description: wlBlankToNull(description),
          po_number: wlBlankToNull(po),
          afe_number: wlBlankToNull(afe),
          cost_code: wlBlankToNull(costCode),
          notes: wlBlankToNull(notes),
        };
        if (ticketMode) body.ticket_mode = ticketMode;
        if (allowScheduled) body.status = status;
        onSubmit(body);
      }}
    >
      <WlFormError message={localError ?? error} />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <WlField label="Well" hint={well ? `Customer: ${well.customerName || '—'}` : 'The job is booked to the well’s customer'}>
          <select className={WL_INPUT} aria-label="Well" value={wellId} onChange={(e) => setWellId(e.target.value)}>
            <option value="">Choose a well</option>
            {wells.map((w) => (
              <option key={w.id} value={w.id}>
                {w.wellName}
                {w.idText ? ` · ${w.idText}` : ''}
                {w.customerName ? ` · ${w.customerName}` : ''}
              </option>
            ))}
          </select>
        </WlField>
        <WlField label="Supervisor">
          <select className={WL_INPUT} aria-label="Supervisor" value={supervisorId} onChange={(e) => setSupervisorId(e.target.value)}>
            <option value="">No supervisor named</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </WlField>
        <WlField label="Ticket mode" hint="Blank uses the customer’s, then the company’s default">
          <select className={WL_INPUT} aria-label="Ticket mode" value={ticketMode} onChange={(e) => setTicketMode(e.target.value)}>
            <option value="">Default</option>
            {(catalog?.ticketModes ?? []).map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </WlField>
        <WlField label="Planned start">
          <input type="date" className={WL_INPUT} aria-label="Planned start" value={start} onChange={(e) => setStart(e.target.value)} />
        </WlField>
        <WlField label="Planned end" hint="The pressure stack is checked against this date">
          <input type="date" className={WL_INPUT} aria-label="Planned end" value={end} onChange={(e) => setEnd(e.target.value)} />
        </WlField>
        {allowScheduled && (
          <WlField label="Create as">
            <select className={WL_INPUT} aria-label="Create as" value={status} onChange={(e) => setStatus(e.target.value as 'draft' | 'scheduled')}>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </WlField>
        )}
        <WlField label="PO number">
          <input className={WL_INPUT} aria-label="PO number" value={po} onChange={(e) => setPo(e.target.value)} />
        </WlField>
        <WlField label="AFE number">
          <input className={WL_INPUT} aria-label="AFE number" value={afe} onChange={(e) => setAfe(e.target.value)} />
        </WlField>
        <WlField label="Cost code">
          <input className={WL_INPUT} aria-label="Cost code" value={costCode} onChange={(e) => setCostCode(e.target.value)} />
        </WlField>
      </div>
      <fieldset>
        <legend className="mb-2 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Run kinds on this job</legend>
        <div className="flex flex-wrap gap-2" data-testid="wl-job-scopes">
          {kinds.map((k) => (
            <label key={k.value} className="inline-flex items-center gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text">
              <input type="checkbox" checked={scopes.includes(k.value)} onChange={() => toggle(k.value)} />
              {k.label}
            </label>
          ))}
          {kinds.length === 0 && <p className="text-sm text-fieldiq-muted">The run kinds did not load.</p>}
        </div>
      </fieldset>
      <WlField label="Description">
        <textarea className={`${WL_INPUT} h-20 py-2`} aria-label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
      </WlField>
      <WlField label="Notes">
        <textarea className={`${WL_INPUT} h-20 py-2`} aria-label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </WlField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WL_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={WL_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
